import { useKorintStepper } from '@folio/components';
import { useDynamicSteps } from '@folio/hooks';
import { cn } from '@folio/lib';
import type { Step } from '@folio/types';
import { Check } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';
import { SuspenseLoader } from '../ApplicationLayout/SuspenseLoader';
import { useAnimatedStepperIndex } from './useAnimatedStepperIndex';
import { Center, Stack } from '@folio/components/ui/flex';

type DisplayStep = { step: Step; visibleIndex: number; dynamicKey?: string; label?: string };

interface StepperProps {
  steps: Array<Step>;
  basePath: string;
  afterEndPath?: string;
  title?: string;
  stepsI18nPrefix?: string;
}

export function Stepper({ steps, basePath, afterEndPath, title, stepsI18nPrefix }: StepperProps) {
  const {
    visualActiveStepIndex,
    maxVisitedVisibleIndex,
    visibleSteps,
    next,
    previous,
    isFirstStep,
    isLastStep,
    replaceParams,
    handleClick,
    invalidateFutureSteps,
  } = useKorintStepper(steps, basePath, afterEndPath);

  const { steps: dynamicSteps } = useDynamicSteps(visibleSteps.some(step => step.isDynamic));
  const [searchParams] = useSearchParams();
  const displaySteps = visibleSteps.flatMap<DisplayStep>((step, visibleIndex) =>
    step.isDynamic && dynamicSteps.length > 0
      ? dynamicSteps.map(({ key, label }) => ({ step, visibleIndex, dynamicKey: key, label }))
      : [{ step, visibleIndex, dynamicKey: undefined, label: undefined }],
  );
  const toDisplayIndex = (visibleIndex: number, lastSubStep: boolean) => {
    const first = displaySteps.findIndex(entry => entry.visibleIndex === visibleIndex);
    if (first === -1 || !displaySteps[first].dynamicKey) return first;
    const subStep = lastSubStep
      ? dynamicSteps.length - 1
      : dynamicSteps.findIndex(({ key }) => key === searchParams.get('step'));
    return first + Math.max(0, subStep);
  };
  const activeDisplayIndex = toDisplayIndex(visualActiveStepIndex, false);
  const maxVisitedDisplayIndex =
    maxVisitedVisibleIndex > visualActiveStepIndex
      ? toDisplayIndex(maxVisitedVisibleIndex, true)
      : activeDisplayIndex;

  const animatedIndex = useAnimatedStepperIndex(activeDisplayIndex);
  const { t } = useTranslation();

  const isSingleStep = displaySteps.length === 1;

  if (isSingleStep) {
    return (
      <SuspenseLoader>
        <Outlet
          context={{
            stepper: {
              next,
              previous,
              isFirstStep,
              isLastStep,
              replaceParams,
              isSingleStep,
              afterEndPath,
              invalidateFutureSteps,
            },
          }}
        />
      </SuspenseLoader>
    );
  }

  const stepperContext = {
    stepper: {
      next,
      previous,
      isFirstStep,
      isLastStep,
      replaceParams,
      isSingleStep,
      afterEndPath,
      invalidateFutureSteps,
    },
  };

  const renderStepCircle = (
    { step, dynamicKey, label: dynamicLabel }: DisplayStep,
    index: number,
  ) => {
    const isCompleted = index < animatedIndex;
    const isActive = index === animatedIndex;
    const isFutureVisited = index > activeDisplayIndex && index <= maxVisitedDisplayIndex;
    const isClickable = index < activeDisplayIndex || isFutureVisited;
    const Icon = step.icon as React.FC<{ className?: string }> | undefined;
    const label =
      dynamicLabel ??
      (stepsI18nPrefix
        ? t(`${stepsI18nPrefix}.${step.path}`, { defaultValue: '', nsSeparator: false })
        : '');
    const search = dynamicKey ? `?step=${dynamicKey}` : undefined;
    const distance = Math.abs(index - animatedIndex);

    return (
      <Fragment key={`${step.path}-${dynamicKey ?? ''}`}>
        <Stack
          className={cn('items-center gap-1.5 flex-shrink-0', isClickable && 'cursor-pointer')}
          onClick={
            isClickable
              ? () => {
                  const originalIndex = steps.findIndex(s => s.path === step.path);
                  if (originalIndex !== -1) handleClick(originalIndex, search);
                }
              : undefined
          }
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={
            isClickable
              ? e => {
                  if (e.key === 'Enter') {
                    const originalIndex = steps.findIndex(s => s.path === step.path);
                    if (originalIndex !== -1) handleClick(originalIndex, search);
                  }
                }
              : undefined
          }
        >
          <div className="relative">
            <div
              className={cn(
                'relative rounded-full flex items-center justify-center transition-all duration-300',
                distance === 0
                  ? 'size-9'
                  : distance === 1
                    ? 'size-6 sm:size-9'
                    : 'size-3 sm:size-9',
                isCompleted
                  ? 'bg-secondary hover:opacity-80'
                  : isActive
                    ? 'bg-secondary'
                    : isFutureVisited
                      ? 'bg-gray-100 ring-2 ring-primary/50 hover:ring-primary'
                      : 'bg-gray-100',
              )}
            >
              <div
                className={cn(
                  distance > 1 ? 'hidden sm:inline-flex' : 'inline-flex',
                  'items-center justify-center',
                )}
              >
                {isCompleted ? (
                  <Check
                    className={cn('text-white', distance === 1 ? 'size-3 sm:size-4' : 'size-4')}
                  />
                ) : isFutureVisited ? (
                  <Check
                    className={cn(
                      'text-primary/50',
                      distance === 1 ? 'size-3 sm:size-4' : 'size-4',
                    )}
                  />
                ) : Icon ? (
                  <Icon
                    className={cn(
                      isActive ? 'text-white' : 'text-gray-400',
                      distance === 1 ? 'size-3 sm:size-4' : 'size-4',
                    )}
                  />
                ) : (
                  <span
                    className={cn(
                      'font-semibold text-xs',
                      isActive ? 'text-white' : 'text-gray-400',
                    )}
                  >
                    {index + 1}
                  </span>
                )}
              </div>
            </div>
          </div>
          {label && (
            <span
              className={cn(
                'hidden sm:block text-xs text-center max-w-[60px] leading-tight',
                isActive ? 'text-foreground font-medium' : 'text-muted-foreground',
              )}
            >
              {label}
            </span>
          )}
        </Stack>

        {index < displaySteps.length - 1 && (
          <div className="flex-1 h-0.5 mt-0 sm:mt-[18px] bg-gray-200 relative overflow-hidden mx-1 sm:mx-2">
            <div
              className={cn(
                'absolute inset-y-0 left-0 bg-secondary transition-[width] duration-500 ease-in-out',
                isCompleted ? 'w-full' : 'w-0',
              )}
            />
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <>
      <div className="md:container mx-auto px-4 py-6">
        {title && <h3 className="text-lg text-center w-full mb-6">{title}</h3>}
        <Center className="sm:items-start w-full">
          {displaySteps.map((entry, index) => renderStepCircle(entry, index))}
        </Center>
      </div>
      <div className="md:container mx-auto px-1 md:px-4">
        <SuspenseLoader>
          <Outlet context={stepperContext} />
        </SuspenseLoader>
      </div>
    </>
  );
}
